import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { ClipboardCheck, FileCheck2, ShieldCheck, Car, Building2, Cpu, LucideIcon, Sparkles, Factory, Server } from 'lucide-react';
import { Button } from '../ui/Button';
import styles from './Proof.module.css';

// Industry icons for case studies
const industryIcons: Record<string, LucideIcon> = {
  'Automotive': Car,
  'Banking': Building2,
  'Finance': Building2,
  'Finanz': Building2,
  'Technology': Cpu,
  'Technologie': Cpu,
  'Retail': Building2,
  'Manufacturing': Factory,
  'Fertigung': Factory,
  'Regulated': ShieldCheck,
  'Reguliert': ShieldCheck,
  'SAP': Server,
};

export const Proof: React.FC = () => {
  const { t } = useLanguage();
  const artifactIcons = [FileCheck2, ShieldCheck, ClipboardCheck];

  return (
    <section className={styles.proof} id="proof">
      <div className={styles.proof__container}>
        <motion.div
          className={styles.proof__header}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.15 }}
        >
          <span className={styles.proof__tag}><Sparkles size={14} />{t.proof.tag}</span>
          <h2 className={styles.proof__title}>{t.proof.title}</h2>
          <p className={styles.proof__subtitle}>{t.proof.subtitle}</p>
        </motion.div>





        <div className={styles.proof__artifacts}>
          <div className={styles.proof__artifactsHeader}>
            <span className={styles.proof__artifactsTag}>{t.proof.artifacts.title}</span>
            <p className={styles.proof__artifactsSubtitle}>{t.proof.artifacts.subtitle}</p>
          </div>
          <div className={styles.proof__artifactsGrid}>
            {t.proof.artifacts.items.map((item, index) => {
              const Icon = artifactIcons[index] ?? FileCheck2;
              return (
                <div key={item.title} className={styles.proof__artifactCard}>
                  <div className={styles.proof__artifactIcon}>
                    <Icon size={20} />
                  </div>
                  <div className={styles.proof__artifactContent}>
                    <h3 className={styles.proof__artifactTitle}>{item.title}</h3>
                    <p className={styles.proof__artifactDesc}>{item.desc}</p>
                  </div>
                  <div className={styles.proof__artifactPreview} aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.proof__cases}>
          {t.proof.cases.map((item, index) => {
            const IndustryIcon = industryIcons[item.industry] || Building2;
            return (
              <motion.div
                key={index}
                className={styles.proof__caseCard}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.15, delay: index * 0.05 }}
              >
                {/* Hero Metric Section */}
                {item.metric && (
                  <div className={styles.proof__caseHero}>
                    <div className={styles.proof__metric}>{item.metric}</div>
                    <div className={styles.proof__metricLabel}>{item.metricLabel}</div>
                    <div className={styles.proof__heroPattern} aria-hidden="true" />
                  </div>
                )}

                <div className={styles.proof__caseHeader}>
                  <div>
                    <h3 className={styles.proof__caseTitle}>{item.title}</h3>
                    <span className={styles.proof__caseIndustry}>
                      <IndustryIcon size={14} />
                      {item.industry}
                    </span>
                  </div>
                  {item.badge && (
                    <span className={styles.proof__badge}>
                      <ShieldCheck size={12} />
                      {item.badge}
                    </span>
                  )}
                </div>
                <div className={styles.proof__caseBody}>
                  {/* Combined content for brevity in new design */}
                  <div className={styles.proof__caseGroup}>
                    <p className={styles.proof__caseLabel}>{t.proof.labels.boardroomChallenge}</p>
                    <p className={styles.proof__caseText}>{item.boardroomChallenge}</p>
                  </div>
                  <div className={styles.proof__caseGroup}>
                    <p className={styles.proof__caseLabel}>{t.proof.labels.impact}</p>
                    <p className={styles.proof__caseText}>{item.impact}</p>
                  </div>
                </div>

                <div className={styles.proof__caseActions}>
                  <Link to="/case-studies" className={styles.proof__readMore}>
                    {t.proof.actions.readCase} <span aria-hidden="true">→</span>
                  </Link>
                </div>
                <div className={styles.proof__watermark} aria-hidden="true">
                  <IndustryIcon strokeWidth={1} />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className={styles.proof__actions}>
          <Button variant="primary" size="md" to="/assessment">
            {t.proof.actions.cta}
          </Button>
        </div>



        <p className={styles.proof__note}>{t.proof.actions.note}</p>
      </div>
    </section>
  );
};

export default Proof;
